import type { ReactNode } from 'react';
import { useBusinessDay } from '../hooks/useBusinessDay';
import { useBusinessNumber } from '../hooks/useBusinessNumber';
import { dealerLabel } from '../contracts/bill-ref';
import type { TaxInvoiceDoc } from '../contracts/tax-invoice';

const REVERSE_CHARGE_NOTE = 'Reverse charge \u2014 tax payable by recipient';

const cell: React.CSSProperties = {
  border: '1px solid var(--color-text-primary)',
  padding: '6px 8px',
  fontSize: 12,
  verticalAlign: 'top',
};
const head: React.CSSProperties = {
  ...cell,
  background: 'var(--color-bg-muted)',
  fontWeight: 600,
  textAlign: 'left',
};
const num: React.CSSProperties = { ...cell, textAlign: 'right' };
const muted: React.CSSProperties = { color: 'var(--color-text-muted)' };

/** The printable tax invoice. Shared so the vendor and the back office read the
 *  same document, never two drifting versions of it. */
export function TaxInvoice({ doc }: { doc: TaxInvoiceDoc }): ReactNode {
  const { day, dayRange } = useBusinessDay();
  // Amounts follow the tenant's DISPLAY_NUMBER_FORMAT, not a locale in the
  // source. `inr` used to live above this component and spelled out 'en-IN',
  // so an invoice grouped its figures its own way whatever the tenant chose.
  const { money } = useBusinessNumber();
  const inr = (n: number): string => `\u20B9${money(n ?? 0)}`;
  const { invoice, supplier, buyer, totals } = doc;
  // The bill is known by the jobs it bills and their dealers, never by its
  // internal claim number.
  const distinct = (values: string[]): string =>
    [...new Set(values.filter(Boolean))].join(', ') || '\u2014';
  const jobs = distinct(doc.lines.map((l) => l.jobNumber));
  const dealers = distinct(
    doc.lines.map((l) => (l.dealerName ? dealerLabel(l.dealerName, l.dealerCode) : '')),
  );

  return (
    <div
      style={{
        maxWidth: 920,
        margin: '0 auto',
        background: 'var(--color-bg-surface)',
        border: '1px solid var(--color-text-primary)',
        fontFamily: 'Segoe UI, Arial, sans-serif',
        color: 'var(--color-text-primary)',
      }}
    >
      <div
        style={{
          textAlign: 'center',
          padding: '10px 0',
          borderBottom: '1px solid var(--color-text-primary)',
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: 1 }}>
          TAX INVOICE
        </div>
        <div style={{ fontSize: 11 }}>
          {invoice.status === 'DRAFT'
            ? 'DRAFT \u2014 NOT YET SUBMITTED'
            : invoice.status}
        </div>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          <tr>
            <td style={{ ...cell, width: '50%' }}>
              <strong>Supplier (From)</strong>
              <div>{supplier.legalName}</div>
              {supplier.name !== supplier.legalName && <div>{supplier.name}</div>}
              <div>
                {supplier.address}
                {supplier.pincode ? ` \u2013 ${supplier.pincode}` : ''}
              </div>
              <div>GSTIN: {supplier.gstin || '\u2014'}</div>
              <div>PAN: {supplier.pan || '\u2014'}</div>
              <div style={muted}>
                {supplier.mobile} {supplier.email}
              </div>
              <div style={muted}>Vendor Code: {supplier.code}</div>
            </td>
            <td style={{ ...cell, width: '50%' }}>
              <strong>Buyer (To)</strong>
              <div>{buyer.legalName || '\u2014'}</div>
              <div>{buyer.tradeName}</div>
              <div>
                {buyer.address}
                {buyer.city ? `, ${buyer.city}` : ''}
                {buyer.pincode ? ` \u2013 ${buyer.pincode}` : ''}
              </div>
              <div>GSTIN: {buyer.gstin || '\u2014'}</div>
            </td>
          </tr>
          <tr>
            <td style={cell}>
              <div>
                <strong>Invoice No:</strong> {invoice.invoiceNumber || '\u2014'}
              </div>
              <div>
                <strong>Invoice Date:</strong> {day(invoice.invoiceDate)}
              </div>
              <div>
                <strong>Job No:</strong> {jobs}
              </div>
              <div>
                <strong>Dealer:</strong> {dealers}
              </div>
            </td>
            <td style={cell}>
              <div>
                <strong>Place of Supply:</strong> {doc.placeOfSupply || '\u2014'}
              </div>
              <div>
                <strong>Supply Type:</strong>{' '}
                {doc.supplyType === 'INTRA_STATE'
                  ? 'Intra-State (CGST + SGST)'
                  : 'Inter-State (IGST)'}
              </div>
              {invoice.billingPeriodFrom && invoice.billingPeriodTo ? (
                <div>
                  <strong>Billing Period:</strong> {dayRange(invoice.billingPeriodFrom, invoice.billingPeriodTo)}
                </div>
              ) : null}
              <div data-testid="invoice-reverse-charge">
                <strong>Tax payable on reverse charge:</strong>{' '}
                {doc.taxPayableOnReverseCharge ? 'Yes' : 'No'}
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={head}>#</th>
            <th style={head}>Description of Service</th>
            <th style={head}>HSN / SAC</th>
            <th style={head}>Work Order / Job</th>
            <th style={head}>Dealer / Site</th>
            <th style={{ ...head, textAlign: 'right' }}>Qty</th>
            <th style={{ ...head, textAlign: 'right' }}>Rate</th>
            <th style={{ ...head, textAlign: 'right' }}>Taxable Value</th>
          </tr>
        </thead>
        <tbody>
          {doc.lines.length === 0 && (
            <tr>
              <td style={cell} colSpan={8}>
                No lines on this bill yet.
              </td>
            </tr>
          )}
          {doc.lines.map(l => (
            <tr key={l.lineId}>
              <td style={cell}>{l.serial}</td>
              <td style={cell}>
                <div>{l.description || l.workTypeCode}</div>
                {l.brandingTypeName && (
                  <div style={muted}>
                    {l.brandingTypeName}
                    {l.brandingItemName ? ` \u2014 ${l.brandingItemName}` : ''}
                  </div>
                )}
                {(l.serviceFrom || l.serviceTo) && (
                  <div style={muted}>Period: {dayRange(l.serviceFrom, l.serviceTo)}</div>
                )}
                {l.dimensions && <div style={muted}>Dimensions: {l.dimensions}</div>}
                {l.remarks && <div style={muted}>Remark: {l.remarks}</div>}
                {l.isReverseCharge && (
                  <div data-testid={`invoice-line-reverse-charge-${l.lineId}`} style={{ fontWeight: 600 }}>
                    {REVERSE_CHARGE_NOTE}: {inr(l.taxAmount)}
                  </div>
                )}
              </td>
              <td style={cell}>{l.hsnSacCode || '\u2014'}</td>
              <td style={cell}>
                <div>{l.workOrderNumber || '\u2014'}</div>
                <div style={muted}>{l.jobNumber}</div>
              </td>
              <td style={cell}>
                <div>{l.dealerName ? dealerLabel(l.dealerName, l.dealerCode) : ''}</div>
                <div style={muted}>{l.location}</div>
              </td>
              <td style={num}>
                {l.areaSqft || l.quantity} {l.uomCode}
              </td>
              <td style={num}>{inr(l.ratePerUnit)}</td>
              <td style={num}>{inr(l.basicAmount)}</td>
            </tr>
          ))}
          {doc.expenses.length > 0 && (
            <tr>
              <td style={{ ...cell, fontWeight: 600 }} colSpan={8}>
                Other expenses
              </td>
            </tr>
          )}
          {doc.expenses.map(e => (
            <tr key={e.expenseId} data-testid={`invoice-expense-${e.expenseId}`}>
              <td style={cell} />
              <td style={cell}>
                <div>{e.expenseTypeName}</div>
                {e.remarks && <div style={muted}>{e.remarks}</div>}
                {e.isReverseCharge && (
                  <div style={{ fontWeight: 600 }}>
                    {REVERSE_CHARGE_NOTE}: {inr(e.taxAmount)}
                  </div>
                )}
              </td>
              <td style={cell}>{e.hsnSacCode || '\u2014'}</td>
              <td style={cell} colSpan={4} />
              <td style={num}>{inr(e.amount)}</td>
            </tr>
          ))}
          <tr>
            <td style={{ ...cell, fontWeight: 600 }} colSpan={7}>
              Total Taxable Value
            </td>
            <td style={{ ...num, fontWeight: 600 }}>{inr(totals.basicAmount)}</td>
          </tr>
        </tbody>
      </table>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={head}>Tax</th>
            <th style={{ ...head, textAlign: 'right' }}>Rate %</th>
            <th style={{ ...head, textAlign: 'right' }}>Taxable Value</th>
            <th style={{ ...head, textAlign: 'right' }}>Tax Amount</th>
          </tr>
        </thead>
        <tbody>
          {doc.taxSummary.length === 0 && (
            <tr>
              <td style={cell} colSpan={4}>
                Tax not calculated yet \u2014 run Calculate before submitting.
              </td>
            </tr>
          )}
          {doc.taxSummary.map(t => (
            <tr key={`${t.taxTypeCode}-${t.ratePct}-${t.reverseCharge ? 'rcm' : 'fcm'}`}>
              <td style={cell}>
                {t.taxTypeCode}
                {t.reverseCharge && <div style={muted}>{REVERSE_CHARGE_NOTE}</div>}
              </td>
              <td style={num}>{t.ratePct}</td>
              <td style={num}>{inr(t.taxableAmount)}</td>
              <td style={num}>{inr(t.taxAmount)}</td>
            </tr>
          ))}
          <tr>
            <td style={{ ...cell, fontWeight: 600 }} colSpan={3}>
              Total Tax
            </td>
            <td style={{ ...num, fontWeight: 600 }}>{inr(totals.totalTax)}</td>
          </tr>
          {totals.reverseChargeTax > 0 && (
            <tr data-testid="invoice-reverse-charge-tax">
              <td style={cell} colSpan={3}>
                of which payable by recipient (reverse charge)
              </td>
              <td style={num}>{inr(totals.reverseChargeTax)}</td>
            </tr>
          )}
          <tr>
            <td style={{ ...cell, fontWeight: 700 }} colSpan={3}>
              {totals.reverseChargeTax > 0 ? 'Grand Total (payable to supplier)' : 'Grand Total'}
            </td>
            <td style={{ ...num, fontWeight: 700 }}>{inr(totals.grandTotal)}</td>
          </tr>
        </tbody>
      </table>

      <div style={{ ...cell, borderTop: 'none' }}>
        <strong>Amount in words:</strong> {totals.amountInWords}
      </div>
      {invoice.remarks && (
        <div style={{ ...cell, borderTop: 'none' }}>
          <strong>Remarks:</strong> {invoice.remarks}
        </div>
      )}
      <div
        style={{
          ...cell,
          borderTop: 'none',
          display: 'flex',
          justifyContent: 'space-between',
          paddingTop: 36,
        }}
      >
        <span>Receiver&apos;s Signature</span>
        <span>For {supplier.legalName}</span>
      </div>
    </div>
  );
}
